import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtMoney, fmtCur } from "@/lib/format";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, MagnifyingGlass, PencilSimple, Trash } from "@phosphor-icons/react";

const empty = { name: "", category: "", norm_hours: 1, price: 0, price_currency: "UAH" };

export default function Services() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/services", { params: { search } }).then((r) => setList(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search]);

  const save = async () => {
    if (!form.name) return toast.error("Вкажіть назву послуги");
    const payload = { ...form, norm_hours: Number(form.norm_hours), price: Number(form.price), price_currency: form.price_currency || "UAH" };
    try {
      if (editId) await api.put(`/services/${editId}`, payload);
      else await api.post("/services", payload);
      toast.success("Збережено"); setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const edit = (s) => { setForm({ name: s.name, category: s.category, norm_hours: s.norm_hours, price: s.price, price_currency: s.price_currency || "UAH" }); setEditId(s.id); setOpen(true); };
  const del = async (id) => { if (!window.confirm("Видалити послугу?")) return; await api.delete(`/services/${id}`); toast.success("Видалено"); load(); };

  return (
    <div>
      <PageHeader title="Послуги та прайс" subtitle={`Позицій: ${list.length}`}
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditId(null); } }}>
            <DialogTrigger asChild><Button data-testid="add-service-btn"><Plus size={18} className="mr-1" /> Послуга</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editId ? "Редагувати послугу" : "Нова послуга"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Назва *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="service-name" /></div>
                <div><Label>Категорія</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1.5" data-testid="service-category" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Нормо-години</Label><Input type="number" step="0.1" value={form.norm_hours} onChange={(e) => setForm({ ...form, norm_hours: e.target.value })} className="mt-1.5" data-testid="service-hours" /></div>
                  <div>
                    <Label>Вартість</Label>
                    <div className="flex gap-2 mt-1.5">
                      <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="flex-1" data-testid="service-price" />
                      <Select value={form.price_currency} onValueChange={(v) => setForm({ ...form, price_currency: v })}>
                        <SelectTrigger className="w-24" data-testid="service-price-currency"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="UAH">₴ ГРН</SelectItem><SelectItem value="USD">$ USD</SelectItem><SelectItem value="EUR">€ EUR</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter><Button onClick={save} data-testid="save-service-btn">Зберегти</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="relative mb-4 max-w-sm">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Пошук послуги..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="services-search" />
      </div>
      <Card>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Послуга</TableHead><TableHead>Категорія</TableHead><TableHead>Нормо-год</TableHead>
            <TableHead className="text-right">Ціна</TableHead><TableHead className="text-right">Дії</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {list.map((s) => (
              <TableRow key={s.id} data-testid={`service-row-${s.id}`}>
                <TableCell className="font-semibold">{s.name}</TableCell>
                <TableCell><span className="text-xs px-2 py-0.5 rounded bg-secondary">{s.category || "—"}</span></TableCell>
                <TableCell>{s.norm_hours}</TableCell>
                <TableCell className="text-right font-semibold">{fmtCur(s.price, s.price_currency)}</TableCell>
                <TableCell className="text-right">
                  <button className="text-muted-foreground hover:text-foreground p-1" onClick={() => edit(s)} data-testid={`edit-service-${s.id}`}><PencilSimple size={18} /></button>
                  <button className="text-muted-foreground hover:text-destructive p-1" onClick={() => del(s.id)} data-testid={`del-service-${s.id}`}><Trash size={18} /></button>
                </TableCell>
              </TableRow>
            ))}
            {list.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Немає послуг</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
